<script lang="ts" setup>
/**
 * DocumentPreview — shared in-app document preview modal.
 *
 * Ported from partner_web_app for customer_web_app so any page that renders
 * a delivery document ("View") consistently handles every file type instead
 * of the old image-inline / iframe-fallback split.
 *
 * Supports:
 *   - Images (png/jpg/gif/webp/svg/heic/…): <img>
 *   - PDFs: native <iframe>
 *   - Office (doc/docx/xls/xlsx/ppt/pptx/rtf/odt): Google Docs viewer
 *   - Text (txt/log/md/csv/json/yaml/xml): fetched + shown in <pre>
 *   - Unknown or failed loads: fallback panel with filename
 *
 * Every kind offers Download; unknown / office fallbacks also expose an
 * "Open in new tab" link so nothing is ever un-viewable.
 *
 * Differences from the partner_web_app twin:
 *   - Icons are inline SVGs (customer_web_app has no lucide-vue-next dep).
 *   - CSS uses concrete colour values from the site's chrome (#12284A navy,
 *     #e2e8f0 border, etc.) instead of design-system vars that don't exist
 *     here.
 *   - The optional review_status pill was stripped — customer-side delivery
 *     docs don't carry a per-doc review state to display.
 */

import { computed, ref, watch } from 'vue'
import {
  previewKind,
  officeViewerUrl,
  fileNameFromUrl,
  prettyDocType,
} from '../utils/docPreview'

interface PreviewSource {
  file_url: string
  doc_type?: string
  uploaded_at?: string
  expires_at?: string | null
}

const props = defineProps<{
  doc: PreviewSource | null
}>()

const emit = defineEmits<{ (e: 'close'): void }>()

const imgFailed = ref(false)
const textContent = ref<string>('')
const textLoading = ref(false)
const textError = ref<string>('')

const kind = computed(() => (props.doc ? previewKind(props.doc.file_url) : 'unknown'))
const fileName = computed(() => (props.doc ? fileNameFromUrl(props.doc.file_url) : 'file'))
const title = computed(() => (props.doc ? prettyDocType(props.doc.doc_type || fileName.value) : ''))

function formatDate(value?: string | null): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

watch(
  () => props.doc?.file_url,
  async (url) => {
    imgFailed.value = false
    textContent.value = ''
    textError.value = ''
    if (!url) return
    if (previewKind(url) !== 'text') return
    textLoading.value = true
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const raw = await res.text()
      textContent.value = raw.length > 500_000 ? raw.slice(0, 500_000) + '\n\n… (truncated)' : raw
    } catch (err: any) {
      textError.value = err?.message || 'Could not fetch this file.'
    } finally {
      textLoading.value = false
    }
  },
  { immediate: true },
)

function onClose() {
  emit('close')
}

function onKeydown(ev: KeyboardEvent) {
  if (ev.key === 'Escape') onClose()
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="doc"
      class="doc-preview"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      @click.self="onClose"
      @keydown="onKeydown"
    >
      <div class="doc-preview__panel">
        <header class="doc-preview__head">
          <div class="doc-preview__title-block">
            <h3 class="doc-preview__title">{{ title }}</h3>
            <span v-if="doc.uploaded_at || doc.expires_at" class="doc-preview__meta">
              <template v-if="doc.uploaded_at">Uploaded {{ formatDate(doc.uploaded_at) }}</template>
              <template v-if="doc.expires_at"> · expires {{ formatDate(doc.expires_at) }}</template>
            </span>
          </div>
          <button
            type="button"
            class="doc-preview__close"
            aria-label="Close preview"
            @click="onClose"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </header>

        <div class="doc-preview__body" :class="`is-${kind}`">
          <img
            v-if="kind === 'image' && !imgFailed"
            :src="doc.file_url"
            :alt="title"
            class="doc-preview__img"
            @error="imgFailed = true"
          />
          <iframe
            v-else-if="kind === 'pdf'"
            :src="doc.file_url"
            :title="title"
            class="doc-preview__frame"
            loading="lazy"
          />
          <iframe
            v-else-if="kind === 'word'"
            :src="officeViewerUrl(doc.file_url)"
            :title="title"
            class="doc-preview__frame"
            loading="lazy"
          />
          <template v-else-if="kind === 'text'">
            <div v-if="textLoading" class="doc-preview__loading">Loading file…</div>
            <div v-else-if="textError" class="doc-preview__nopreview">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
              <p><strong>{{ fileName }}</strong></p>
              <p class="doc-preview__nopreview-hint">Could not load preview: {{ textError }}</p>
            </div>
            <pre v-else class="doc-preview__text">{{ textContent }}</pre>
          </template>
          <div v-else class="doc-preview__nopreview">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
            <p><strong>{{ fileName }}</strong></p>
            <p class="doc-preview__nopreview-hint">
              {{ imgFailed
                ? 'Preview failed to load. Download or open in a new tab.'
                : 'No inline preview for this file type. Download to view.' }}
            </p>
          </div>
        </div>

        <footer class="doc-preview__foot">
          <a
            :href="doc.file_url"
            :download="fileName"
            class="doc-preview__btn doc-preview__btn--ghost"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Download
          </a>
          <a
            :href="doc.file_url"
            target="_blank"
            rel="noopener"
            class="doc-preview__btn doc-preview__btn--primary"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            Open in new tab
          </a>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.doc-preview {
  position: fixed;
  inset: 0;
  background: rgba(10, 20, 40, 0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  backdrop-filter: blur(4px);
  font-family: 'DM Sans', sans-serif;
}
.doc-preview__panel {
  width: min(1080px, 100%);
  max-height: 92vh;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.3);
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  overflow: hidden;
}
.doc-preview__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1.5px solid #f1f5f9;
}
.doc-preview__title-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}
.doc-preview__title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #12284A;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.doc-preview__meta {
  font-size: 0.72rem;
  color: #94a3b8;
  font-weight: 500;
}
.doc-preview__close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;
}
.doc-preview__close:hover { background: #fee2e2; color: #dc2626; }

.doc-preview__body {
  /* Deep charcoal so image thumbnails read as "on a stage" without
     inheriting whatever background the surrounding page uses. */
  background: #0f172a;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  min-height: 320px;
  height: 65vh;
  position: relative;
}
.doc-preview__body.is-pdf,
.doc-preview__body.is-word { background: #F5F6FA; }
.doc-preview__body.is-text { background: #ffffff; align-items: stretch; justify-content: stretch; }

.doc-preview__img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}
.doc-preview__frame {
  width: 100%;
  height: 100%;
  border: 0;
  background: #ffffff;
}
.doc-preview__text {
  margin: 0;
  padding: 18px 20px;
  width: 100%;
  height: 100%;
  overflow: auto;
  background: #ffffff;
  color: #1e293b;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.78rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}
.doc-preview__loading {
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.85rem;
}
.doc-preview__nopreview {
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 32px;
  text-align: center;
}
.doc-preview__nopreview strong {
  color: #ffffff;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.85rem;
  word-break: break-all;
}
.doc-preview__nopreview-hint {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.55);
  margin: 0;
}

.doc-preview__foot {
  padding: 12px 18px;
  border-top: 1.5px solid #f1f5f9;
  background: #ffffff;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.doc-preview__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  cursor: pointer;
}
.doc-preview__btn--ghost {
  color: #12284A;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
}
.doc-preview__btn--ghost:hover {
  background: #f8fafc;
  border-color: #12284A;
}
.doc-preview__btn--primary {
  color: #ffffff;
  background: linear-gradient(135deg, #12284A 0%, #1e3f73 100%);
  border: 1.5px solid transparent;
}
.doc-preview__btn--primary:hover { opacity: 0.92; }
</style>
